import type { RandomSource } from './random.ts';
import type { RoundConfig } from './rules.ts';
import { generatePrepareDelayMs, generateTargetMs } from './target.ts';

export type RoundPhase = 'idle' | 'target' | 'prepare' | 'active' | 'submitted' | 'reveal';

export interface RoundState {
  readonly phase: RoundPhase;
  readonly targetMs: number | null;
  readonly prepareDelayMs: number | null;
  readonly prepareStartedAtMs: number | null;
  readonly activeStartedAtMs: number | null;
  readonly stoppedAtMs: number | null;
  readonly elapsedMs: number | null;
  readonly errorMs: number | null;
  readonly isFalseStart: boolean;
}

export const IDLE_ROUND_STATE: RoundState = {
  phase: 'idle',
  targetMs: null,
  prepareDelayMs: null,
  prepareStartedAtMs: null,
  activeStartedAtMs: null,
  stoppedAtMs: null,
  elapsedMs: null,
  errorMs: null,
  isFalseStart: false,
};

export type RoundEvent =
  | { type: 'SHOW_TARGET' }
  | { type: 'BEGIN_PREPARE'; now: number }
  | { type: 'ACTIVATE'; now: number }
  | { type: 'STOP'; now: number }
  | { type: 'FALSE_START'; now: number }
  | { type: 'REVEAL' }
  | { type: 'RESET' };

export type RoundTransitionResult =
  | { ok: true; state: RoundState }
  | { ok: false; reason: string; state: RoundState };

function reject(state: RoundState, reason: string): RoundTransitionResult {
  return { ok: false, reason, state };
}

/**
 * Pure guarded reducer for one round. Every transition is validated against the
 * current phase so invalid, duplicate or early inputs are rejected rather than
 * silently accepted.
 */
export function applyRoundEvent(
  state: RoundState,
  event: RoundEvent,
  random: RandomSource,
  config: RoundConfig,
): RoundTransitionResult {
  switch (event.type) {
    case 'SHOW_TARGET': {
      if (state.phase !== 'idle') return reject(state, `cannot show target from phase "${state.phase}"`);
      return {
        ok: true,
        state: {
          ...IDLE_ROUND_STATE,
          phase: 'target',
          targetMs: generateTargetMs(random, config),
          prepareDelayMs: generatePrepareDelayMs(random, config),
        },
      };
    }

    case 'BEGIN_PREPARE': {
      if (state.phase !== 'target') return reject(state, `cannot begin prepare from phase "${state.phase}"`);
      return { ok: true, state: { ...state, phase: 'prepare', prepareStartedAtMs: event.now } };
    }

    case 'ACTIVATE': {
      if (state.phase !== 'prepare') return reject(state, `cannot activate from phase "${state.phase}"`);
      const elapsedSincePrepare = event.now - (state.prepareStartedAtMs ?? event.now);
      if (elapsedSincePrepare < (state.prepareDelayMs ?? 0)) {
        return reject(state, 'cannot activate before the hidden prepare delay has elapsed');
      }
      return { ok: true, state: { ...state, phase: 'active', activeStartedAtMs: event.now } };
    }

    case 'STOP': {
      if (state.phase !== 'active') return reject(state, `cannot stop from phase "${state.phase}"`);
      const activeStartedAtMs = state.activeStartedAtMs ?? event.now;
      const elapsedMs = event.now - activeStartedAtMs;
      const errorMs = Math.abs(elapsedMs - (state.targetMs ?? 0));
      return {
        ok: true,
        state: { ...state, phase: 'submitted', stoppedAtMs: event.now, elapsedMs, errorMs },
      };
    }

    case 'FALSE_START': {
      if (state.phase !== 'prepare') return reject(state, `cannot false-start from phase "${state.phase}"`);
      return {
        ok: true,
        state: {
          ...state,
          phase: 'submitted',
          stoppedAtMs: event.now,
          elapsedMs: event.now - (state.prepareStartedAtMs ?? event.now),
          errorMs: Number.POSITIVE_INFINITY,
          isFalseStart: true,
        },
      };
    }

    case 'REVEAL': {
      if (state.phase !== 'submitted') return reject(state, `cannot reveal from phase "${state.phase}"`);
      return { ok: true, state: { ...state, phase: 'reveal' } };
    }

    case 'RESET': {
      return { ok: true, state: IDLE_ROUND_STATE };
    }

    default: {
      const exhaustive: never = event;
      return reject(state, `unknown event ${JSON.stringify(exhaustive)}`);
    }
  }
}

export interface Attempt {
  readonly targetMs: number;
  readonly elapsedMs: number;
  readonly errorMs: number;
  readonly isFalseStart: boolean;
}

export function attemptFromRoundState(state: RoundState): Attempt {
  if (state.targetMs === null || state.elapsedMs === null || state.errorMs === null) {
    throw new Error('round has not produced a completed attempt yet');
  }
  return {
    targetMs: state.targetMs,
    elapsedMs: state.elapsedMs,
    errorMs: state.errorMs,
    isFalseStart: state.isFalseStart,
  };
}
